/**
 * SupabaseStorageProvider — production storage provider.
 *
 * Delegates to the Supabase Storage API via the existing
 * Supabase client singleton. No vendor-specific logic beyond
 * the Supabase client SDK — the StorageProvider interface
 * isolates all implementation details.
 */

import { getSupabase } from '@/lib/supabase';
import type {
  StorageProvider,
  StorageBucket,
  FileMetadata,
  ListOptions,
  ListResult,
  SignedUrlOptions,
  StoredFile,
} from './types';

class SupabaseStorageProviderImpl implements StorageProvider {
  readonly name = 'supabase';

  async upload(
    bucket: StorageBucket,
    path: string,
    body: Blob | ArrayBuffer,
    mimeType: string,
    options?: { upsert?: boolean; onProgress?: (p: number) => void; signal?: AbortSignal },
  ): Promise<{ path: string; url: string }> {
    const supabase = getSupabase();
    const { data, error } = await supabase.storage
      .from(bucket)
      .upload(path, body, {
        contentType: mimeType,
        upsert: options?.upsert ?? false,
      });

    if (error) throw new Error(`Supabase upload failed: ${error.message}`);
    if (!data) throw new Error('Supabase upload returned no data.');

    const url = supabase.storage.from(bucket).getPublicUrl(path).data.publicUrl;
    if (options?.onProgress) options.onProgress(100);
    return { path: data.path, url };
  }

  async downloadUrl(
    bucket: StorageBucket,
    path: string,
    options?: SignedUrlOptions,
  ): Promise<string> {
    const supabase = getSupabase();

    if (options?.expiresIn && options.expiresIn > 0) {
      const { data, error } = await supabase.storage
        .from(bucket)
        .createSignedUrl(path, options.expiresIn, {
          download: options.download,
        });
      if (error) throw new Error(`Signed URL failed: ${error.message}`);
      return data?.signedUrl ?? '';
    }

    const { data } = supabase.storage.from(bucket).getPublicUrl(path);
    return data.publicUrl;
  }

  async delete(bucket: StorageBucket, path: string): Promise<void> {
    const supabase = getSupabase();
    const { error } = await supabase.storage.from(bucket).remove([path]);
    if (error) throw new Error(`Delete failed: ${error.message}`);
  }

  async move(
    from: { bucket: StorageBucket; path: string },
    to: { bucket: StorageBucket; path: string },
  ): Promise<void> {
    const supabase = getSupabase();
    const { error } = await supabase.storage.from(from.bucket).move(from.path, to.path);
    if (error) throw new Error(`Move failed: ${error.message}`);
  }

  async copy(
    from: { bucket: StorageBucket; path: string },
    to: { bucket: StorageBucket; path: string },
  ): Promise<void> {
    const supabase = getSupabase();
    const { error } = await supabase.storage.from(from.bucket).copy(from.path, to.path);
    if (error) throw new Error(`Copy failed: ${error.message}`);
  }

  async exists(bucket: StorageBucket, path: string): Promise<boolean> {
    const supabase = getSupabase();
    const { data, error } = await supabase.storage
      .from(bucket)
      .list(path.slice(0, path.lastIndexOf('/') || undefined), {
        limit: 1000,
        search: path.split('/').pop() ?? '',
      });
    if (error) return false;
    const filename = path.split('/').pop();
    return (data ?? []).some((item) => item.name === filename);
  }

  async metadata(bucket: StorageBucket, path: string): Promise<FileMetadata | null> {
    const supabase = getSupabase();
    const folderPath = path.slice(0, path.lastIndexOf('/') || undefined);
    const filename = path.split('/').pop() ?? path;
    const { data, error } = await supabase.storage
      .from(bucket)
      .list(folderPath, { limit: 1000, search: filename });

    if (error || !data) return null;
    const item = data.find((f) => f.name === filename);
    if (!item) return null;

    return {
      name: item.name,
      size: item.metadata?.size ?? 0,
      mimeType: item.metadata?.mimetype ?? 'application/octet-stream',
      lastModified: item.updated_at,
    };
  }

  async list(bucket: StorageBucket, options?: ListOptions): Promise<ListResult> {
    const supabase = getSupabase();
    const limit = options?.limit ?? 50;
    const offset = options?.offset ?? 0;

    const { data, error } = await supabase.storage
      .from(bucket)
      .list(options?.prefix ?? '', {
        limit,
        offset,
        sortBy: options?.sortBy
          ? { column: options.sortBy, order: options.sortOrder ?? 'asc' }
          : undefined,
      });

    if (error) throw new Error(`List failed: ${error.message}`);

    const files: StoredFile[] = (data ?? [])
      .filter((item) => item.id !== null)
      .map((item) => ({
        id: `${bucket}/${options?.prefix ?? ''}${item.name}`,
        bucket,
        path: `${options?.prefix ?? ''}${item.name}`,
        name: item.name,
        originalName: item.name,
        mimeType: item.metadata?.mimetype ?? 'application/octet-stream',
        size: item.metadata?.size ?? 0,
        url: supabase.storage.from(bucket).getPublicUrl(`${options?.prefix ?? ''}${item.name}`).data.publicUrl,
        uploadedAt: item.created_at ?? new Date().toISOString(),
        accessLevel: 'public' as const,
        category: 'attachment' as const,
      }));

    return {
      files,
      total: files.length,
      hasMore: files.length === limit,
    };
  }

  async isAvailable(): Promise<boolean> {
    try {
      getSupabase();
      return true;
    } catch {
      return false;
    }
  }
}

export const SupabaseStorageProvider = new SupabaseStorageProviderImpl();
